import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ServerIcon,
  CircleStackIcon,
  CpuChipIcon,
  CloudIcon,
  ClockIcon,
  BoltIcon,
  ExclamationCircleIcon,
  SignalIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// --- Types ---

interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks: Record<string, {
    status: string;
    latency?: number;
    message?: string;
    details?: Record<string, unknown>;
  }>;
  uptime: number;
  startTime: string;
  version: string;
  timestamp: string;
}

interface RequestEntry {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  statusCode: number;
  responseTime: number;
  ip: string;
  userId?: string;
}

interface RequestStats {
  totalRequests: number;
  requestsPerSecond: number;
  avgResponseTime: number;
  errorRate: number;
  topEndpoints: { endpoint: string; count: number; avgTime: number }[];
  statusBreakdown: Record<string, number>;
}

interface TrackedError {
  id: string;
  timestamp: string;
  message: string;
  name: string;
  stack: string;
  fingerprint: string;
  severity: string;
  context: Record<string, unknown>;
  occurrences: number;
}

interface ErrorStats {
  totalErrors: number;
  errorRate: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  trending: { fingerprint: string; message: string; count: number; lastSeen: string }[];
}

interface MonitoringSummary {
  health: HealthCheck;
  requestStats: RequestStats;
  errorStats: ErrorStats;
  recentErrors: TrackedError[];
  recentRequests: RequestEntry[];
  slowRequests: RequestEntry[];
  metrics: {
    counters: Record<string, Record<string, number>>;
    histograms: Record<string, Record<string, { count: number; sum: number; avg: number }>>;
    gauges: Record<string, Record<string, number>>;
  };
}

// --- Helpers ---

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function methodColor(method: string): string {
  switch (method) {
    case 'GET': return 'bg-blue-100 text-blue-800';
    case 'POST': return 'bg-green-100 text-green-800';
    case 'PUT': return 'bg-yellow-100 text-yellow-800';
    case 'DELETE': return 'bg-red-100 text-red-800';
    case 'PATCH': return 'bg-purple-100 text-purple-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

function statusColor(code: number): string {
  if (code < 300) return 'text-green-600';
  if (code < 400) return 'text-yellow-600';
  if (code < 500) return 'text-orange-600';
  return 'text-red-600';
}

function severityColor(severity: string): string {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-800';
    case 'high': return 'bg-orange-100 text-orange-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'low': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

function healthStatusColor(status: string): string {
  switch (status) {
    case 'healthy': return 'bg-green-50 border-green-200';
    case 'degraded': return 'bg-yellow-50 border-yellow-200';
    case 'unhealthy': return 'bg-red-50 border-red-200';
    default: return 'bg-gray-50 border-gray-200';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'healthy':
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    case 'degraded':
      return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    default:
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
  }
}

function getServiceIcon(name: string) {
  switch (name) {
    case 'database': return <CircleStackIcon className="h-5 w-5" />;
    case 'redis': return <CpuChipIcon className="h-5 w-5" />;
    case 'externalApis': return <CloudIcon className="h-5 w-5" />;
    case 'memory': return <CpuChipIcon className="h-5 w-5" />;
    case 'disk': return <ServerIcon className="h-5 w-5" />;
    default: return <ServerIcon className="h-5 w-5" />;
  }
}

// --- Tab Components ---

function OverviewTab({ data }: { data: MonitoringSummary }) {
  const { health, requestStats, errorStats, metrics } = data;

  const memoryGauges = metrics.gauges?.memory_usage_bytes || {};
  const memoryData = Object.entries(memoryGauges).map(([key, value]) => {
    const label = key.replace('type="', '').replace('"', '');
    return { name: label, value: Math.round(value / (1024 * 1024)) };
  });

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Requests/sec</p>
              <p className="text-2xl font-bold text-gray-900">{requestStats.requestsPerSecond}</p>
            </div>
            <SignalIcon className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Error Rate</p>
              <p className="text-2xl font-bold text-gray-900">{requestStats.errorRate}%</p>
            </div>
            <ExclamationCircleIcon className="h-8 w-8 text-red-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Response</p>
              <p className="text-2xl font-bold text-gray-900">{requestStats.avgResponseTime}ms</p>
            </div>
            <BoltIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Errors</p>
              <p className="text-2xl font-bold text-gray-900">{errorStats.totalErrors}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-purple-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-teal-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Uptime</p>
              <p className="text-2xl font-bold text-gray-900">{formatUptime(health.uptime)}</p>
            </div>
            <ClockIcon className="h-8 w-8 text-teal-400" />
          </div>
        </div>
      </div>

      {/* Health Checks */}
      <div className={`rounded-lg p-4 border ${healthStatusColor(health.status)}`}>
        <div className="flex items-center mb-4">
          {getStatusIcon(health.status)}
          <h3 className="ml-2 text-lg font-semibold text-gray-900">
            System: {health.status === 'healthy' ? 'All Systems Operational' : health.status === 'degraded' ? 'Degraded Performance' : 'System Unhealthy'}
          </h3>
          <span className="ml-auto text-sm text-gray-500">v{health.version}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(health.checks).map(([name, check]) => (
            <div
              key={name}
              className={`bg-white rounded-lg p-3 border-l-4 ${
                check.status === 'healthy' ? 'border-green-500' :
                check.status === 'degraded' ? 'border-yellow-500' : 'border-red-500'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center">
                  {getServiceIcon(name)}
                  <span className="ml-2 text-sm font-medium text-gray-900 capitalize">
                    {name.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>
                {getStatusIcon(check.status)}
              </div>
              {check.latency !== undefined && (
                <p className="text-xs text-gray-500">{check.latency}ms</p>
              )}
              {check.message && (
                <p className="text-xs text-gray-400 truncate">{check.message}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Memory Usage Chart */}
      {memoryData.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Memory Usage (MB)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={memoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                formatter={(value: number) => [`${value} MB`, 'Usage']}
              />
              <Area type="monotone" dataKey="value" stroke="#6366f1" fill="#a5b4fc" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Endpoints */}
      {requestStats.topEndpoints.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Endpoints (last minute)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Endpoint</th>
                  <th className="pb-2 text-right">Requests</th>
                  <th className="pb-2 text-right">Avg Time</th>
                </tr>
              </thead>
              <tbody>
                {requestStats.topEndpoints.map((ep, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 font-mono text-xs">{ep.endpoint}</td>
                    <td className="py-2 text-right">{ep.count}</td>
                    <td className="py-2 text-right">{ep.avgTime}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RequestLogTab({ data }: { data: MonitoringSummary }) {
  const [searchPath, setSearchPath] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: filteredData, isLoading } = useQuery({
    queryKey: ['monitoring-requests', searchPath, methodFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '100' });
      if (searchPath) params.set('path', searchPath);
      if (methodFilter) params.set('method', methodFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API_URL}/monitoring/requests?${params}`, { headers: headers() });
      return res.data.requests as RequestEntry[];
    },
    refetchInterval: 30000,
    initialData: data.recentRequests,
  });

  const requests = filteredData || data.recentRequests;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by path..."
              value={searchPath}
              onChange={e => setSearchPath(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <select
            value={methodFilter}
            onChange={e => setMethodFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Methods</option>
            {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Statuses</option>
            <option value="2">2xx Success</option>
            <option value="3">3xx Redirect</option>
            <option value="4">4xx Client Error</option>
            <option value="5">5xx Server Error</option>
          </select>
        </div>
      </div>

      {/* Request Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading requests...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Time</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">Method</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">URL</th>
                  <th className="text-right px-4 py-3 text-gray-500 font-medium">Status</th>
                  <th className="text-right px-4 py-3 text-gray-500 font-medium">Duration</th>
                  <th className="text-left px-4 py-3 text-gray-500 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(req.timestamp).toLocaleTimeString()}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${methodColor(req.method)}`}>
                        {req.method}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs truncate max-w-xs">{req.url}</td>
                    <td className={`px-4 py-2 text-right font-medium ${statusColor(req.statusCode)}`}>
                      {req.statusCode}
                    </td>
                    <td className={`px-4 py-2 text-right ${req.responseTime > 1000 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                      {req.responseTime}ms
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{req.ip}</td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No requests logged yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Slow Requests */}
      {data.slowRequests.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 text-orange-500 mr-2" />
            Slow Requests (&gt;1s)
          </h3>
          <div className="space-y-2">
            {data.slowRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${methodColor(req.method)}`}>
                    {req.method}
                  </span>
                  <span className="font-mono text-xs">{req.url}</span>
                </div>
                <span className="text-red-600 font-medium text-sm">{req.responseTime}ms</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorTrackerTab({ data }: { data: MonitoringSummary }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: errors, refetch } = useQuery({
    queryKey: ['monitoring-errors'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/monitoring/errors?limit=100`, { headers: headers() });
      return res.data.errors as TrackedError[];
    },
    refetchInterval: 30000,
    initialData: data.recentErrors,
  });

  const handleClearErrors = async () => {
    if (!confirm('Are you sure you want to clear all tracked errors?')) return;
    try {
      await axios.delete(`${API_URL}/monitoring/errors`, { headers: headers() });
      refetch();
    } catch (err) {
      console.error('Failed to clear errors:', err);
    }
  };

  const { errorStats } = data;

  return (
    <div className="space-y-6">
      {/* Error Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-500">Total Errors (5min)</p>
          <p className="text-2xl font-bold text-red-600">{errorStats.totalErrors}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-500">Error Rate</p>
          <p className="text-2xl font-bold text-gray-900">{errorStats.errorRate}/s</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-500">By Severity</p>
          <div className="flex gap-2 mt-1">
            {Object.entries(errorStats.bySeverity).map(([sev, count]) => (
              <span key={sev} className={`px-2 py-0.5 rounded text-xs font-medium ${severityColor(sev)}`}>
                {sev}: {count}
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-500">By Type</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(errorStats.byType).map(([type, count]) => (
              <span key={type} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                {type}: {count}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Trending Errors */}
      {errorStats.trending.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trending Errors</h3>
          <div className="space-y-2">
            {errorStats.trending.map((t, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-gray-700 truncate max-w-md">{t.message}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-red-600">{t.count}x</span>
                  <span className="text-xs text-gray-400">{new Date(t.lastSeen).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Recent Errors</h3>
          <button
            onClick={handleClearErrors}
            className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Clear All
          </button>
        </div>
        <div className="divide-y">
          {(errors || []).map(err => (
            <div key={err.id} className="p-4">
              <div
                className="flex items-start justify-between cursor-pointer"
                onClick={() => setExpandedId(expandedId === err.id ? null : err.id)}
              >
                <div className="flex items-start gap-3">
                  {expandedId === err.id
                    ? <ChevronDownIcon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                    : <ChevronRightIcon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                  }
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColor(err.severity)}`}>
                        {err.severity}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{err.name}</span>
                      {err.occurrences > 1 && (
                        <span className="text-xs text-gray-500">({err.occurrences}x)</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{err.message}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {new Date(err.timestamp).toLocaleString()}
                </span>
              </div>
              {expandedId === err.id && (
                <div className="mt-3 ml-7">
                  <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-48">
                    {err.stack}
                  </pre>
                  {Object.keys(err.context).length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-500 mb-1">Context:</p>
                      <pre className="text-xs bg-gray-50 p-2 rounded-lg overflow-x-auto">
                        {JSON.stringify(err.context, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {(!errors || errors.length === 0) && (
            <div className="p-8 text-center text-gray-500">
              No errors tracked
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Main Component ---

type TabKey = 'overview' | 'requests' | 'errors';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'requests', label: 'Request Log' },
  { key: 'errors', label: 'Error Tracker' },
];

export default function Monitoring() {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  const { data, isLoading, refetch, isFetching } = useQuery<MonitoringSummary>({
    queryKey: ['monitoring-summary'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/monitoring/summary`, { headers: headers() });
      return res.data;
    },
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg" />
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring</h1>
          <p className="text-gray-500 mt-1">Real-time application observability and diagnostics</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab data={data} />}
      {activeTab === 'requests' && <RequestLogTab data={data} />}
      {activeTab === 'errors' && <ErrorTrackerTab data={data} />}
    </div>
  );
}
